import { ApolloClient, InMemoryCache, createHttpLink, from, split } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { getIdToken } from './firebase';

const httpUri = process.env.REACT_APP_HASURA_URL || 'https://backend.services-test.pempem.org/graphql';
const wsUri = httpUri.replace('https', 'wss').replace('http', 'ws');

// HTTP link for queries and mutations
const httpLink = createHttpLink({
  uri: httpUri,
});

// Auth link to inject token
const authLink = setContext(async (_, { headers }) => {
  const token = await getIdToken();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'x-hasura-role': headers?.role ?? 'operator',
    },
  };
});

// WebSocket link for subscriptions
const wsLink = new GraphQLWsLink(
  createClient({
    url: wsUri,
    connectionParams: async () => {
      const token = await getIdToken();
      return {
        headers: {
          authorization: token ? `Bearer ${token}` : '',
          'x-hasura-role': 'operator',
        },
      };
    },
  })
);

// Split links based on operation type
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  from([authLink, httpLink])
);

export const apolloClient = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
});

interface ApolloProviderWrapperProps {
  children: React.ReactNode;
}

export const ApolloProviderWrapper: React.FC<ApolloProviderWrapperProps> = ({ children }) => {
  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};
